// src/screens/DeleteAccountScreen.js
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Footer from '../components/Footer';
// --- 1. ADD THIS IMPORT ---
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function DeleteAccountScreen({ navigation }) {
  const [usernameInput, setUsernameInput] = useState('');
  const [gamertagInput, setGamertagInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState(null);

  const auth = getAuth();
  const user = auth.currentUser;

  // This useEffect hook is fine, no changes needed here.
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) {
        setError("You must be logged in to perform this action.");
        return;
      }
      setIsLoading(true);
      try {
        const privateDocRef = doc(db, 'users', user.uid);
        const publicDocRef = doc(db, 'public_profiles', user.uid);
        const [privateDocSnap, publicDocSnap] = await Promise.all([
          getDoc(privateDocRef),
          getDoc(publicDocRef),
        ]);

        if (privateDocSnap.exists() && publicDocSnap.exists()) {
          setProfileData({
            private: privateDocSnap.data(),
            public: publicDocSnap.data(),
          });
        } else {
          setError("Could not load your profile data to verify deletion.");
        }
      } catch (e) {
        setError("An error occurred while fetching your profile data.");
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    loadUserData();
  }, [user]);

  // --- 2. THE LOGIC HERE IS UPDATED ---
  const handleDeletionRequest = async () => {
    if (!usernameInput || !gamertagInput) {
      setError('Please fill out both fields to confirm.');
      return;
    }
    if (!profileData) {
      setError('Your profile data is still loading. Please wait a moment.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // --- Validation Step (No changes needed here) ---
      const correctUsername = profileData.public.userName;
      const linkedGamerTags = Object.values(profileData.private.verification?.games || {}).map(game => game.gamerTag);
      const isUsernameMatch = usernameInput.trim() === correctUsername;
      const isGamerTagMatch = linkedGamerTags.includes(gamertagInput.trim());

      if (!isUsernameMatch || !isGamerTagMatch) {
        throw new Error("The Username or Game Account ID is incorrect. Please try again.");
      }

      // --- NEW: Call the Cloud Function ---
      const functions = getFunctions();
      const requestDeletionFunction = httpsCallable(functions, 'requestAccountDeletion');

      // This call triggers your backend code to disable the user and send the email
      await requestDeletionFunction();

      // Sign out AFTER the function call succeeds
      await signOut(auth);

      // Navigate to the final screen
      navigation.replace('AccountPendingDeletion');

    } catch (e) {
      // Display a more user-friendly error from the function
      setError(e.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  // The return part (JSX) is fine, no changes needed.
  return (
    <View style={styles.outerContainer}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <Text style={styles.title}>Request Account Deletion</Text>
        <Text style={styles.warningText}>
          <Text style={styles.boldWarning}>Important:</Text> Before proceeding, please ensure you have downloaded any financial statements you may need.
        </Text>
        <Text style={styles.warningText}>
          To confirm this permanent action, please enter your exact CritCash Username and one of your linked Game Account IDs.
        </Text>
        {error ? <Text style={styles.errorText}>{error}</Text> : null}
        <TextInput
          style={styles.input}
          placeholder="Enter your CritCash Username"
          placeholderTextColor="#888"
          value={usernameInput}
          onChangeText={setUsernameInput}
          autoCapitalize="none"
        />
        <TextInput
          style={styles.input}
          placeholder="Enter a linked Game Account ID"
          placeholderTextColor="#888"
          value={gamertagInput}
          onChangeText={setGamertagInput}
          autoCapitalize="none"
        />
        <TouchableOpacity
          style={styles.confirmButton}
          onPress={handleDeletionRequest}
          disabled={isLoading || !profileData}
        >
          {isLoading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.buttonText}>Request Deletion & Log Out</Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
          <Text style={styles.cancelLink}>Cancel</Text>
        </TouchableOpacity>
      </ScrollView>
      <Footer navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: { flex: 1, backgroundColor: '#000' },
  scrollContainer: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  title: { fontSize: 24, fontWeight: 'bold', color: '#E74C3C', marginBottom: 20, textAlign: 'center' },
  warningText: { color: '#ccc', textAlign: 'center', marginBottom: 20, lineHeight: 22, maxWidth: 400 },
  boldWarning: { fontWeight: 'bold', color: '#FCD000' },
  input: { width: '100%', backgroundColor: '#222', color: '#fff', padding: 15, marginBottom: 20, borderRadius: 8, fontSize: 16 },
  confirmButton: { backgroundColor: '#E74C3C', width: '100%', padding: 15, borderRadius: 8, alignItems: 'center' },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  cancelLink: { color: '#888', marginTop: 25, fontSize: 14, textDecorationLine: 'underline' },
  errorText: { color: 'salmon', marginBottom: 15, textAlign: 'center', fontSize: 16 },
});